import { graphqlQuery } from '@/lib/api/graphql';
import {
  buildGraphQLSortVariable,
  computeMockPagination,
  createDefaultPagination,
  type GraphQLConnection,
  type PaginationResult,
} from '@/lib/graphql-pagination';
import { generateMockCustomers } from '@/lib/mock-data/ageing-analysis-mock-data';
import { useApiConfig } from '@/providers/ApiConfigProvider';
import type { AgeingAnalysisFilters, CustomerAgeingData } from '@/types/ageing-analysis';
import { useModeAwareQuery } from './useModeAwareQuery';

// --- Params ---

export interface GetAgeingAnalysisParams {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: AgeingAnalysisFilters;
}

// --- GraphQL types ---

interface AgeingAnalysisGraphQLNode {
  customerId: number;
  customerName: string | null;
  accountNumber: string | null;
  currencyCode: string | null;
  creditLimit: number;
  currencyCreditLimit: number;
  riskLevelCode: string | null;
  collectionStrategyName: string | null;
  creditControllerName: string | null;
  accountManagerName: string | null;
  paymentTermsCode: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  lastPaymentDate: string | null;
  userSortKey1: string | null;
  userSortKey2: string | null;
  userSortKey3: string | null;
  userSortKey4: string | null;
  agingBandId: number;
  sequence: number;
  label: string | null;
  bandLabel: string | null;
  startDays: number;
  endDays: number | null;
  totalOutstanding: number;
  currencyTotalOutstanding: number;
  transactionCount: number;
  homeCurrencyCode: string | null;
  customerTotalOutstanding: number;
  customerCurrencyTotalOutstanding: number;
}

interface AgeingAnalysisListResponse {
  customerAgeingAnalysis: {
    customerAgeingAnalysisList: GraphQLConnection<AgeingAnalysisGraphQLNode>;
  };
}

interface AgeingAnalysisCountResponse {
  customerAgeingAnalysis: {
    customerAgeingAnalysisCount: number;
  };
}

// --- GraphQL queries ---

const AGEING_ANALYSIS_LIST_QUERY = `
  query AgeingAnalysisList($first: Int, $after: String, $sort: [CustomerAgeingAnalysisSortSortByInput!], $filter: CustomerAgeingAnalysisFilterInput) {
    customerAgeingAnalysis {
      customerAgeingAnalysisList(first: $first, after: $after, sort: $sort, filter: $filter) {
        edges {
          node {
            customerId
            customerName
            accountNumber
            currencyCode
            creditLimit
            currencyCreditLimit
            riskLevelCode
            collectionStrategyName
            creditControllerName
            accountManagerName
            paymentTermsCode
            contactEmail
            contactPhone
            lastPaymentDate
            userSortKey1
            userSortKey2
            userSortKey3
            userSortKey4
            agingBandId
            sequence
            label
            bandLabel
            startDays
            endDays
            totalOutstanding
            currencyTotalOutstanding
            transactionCount
            homeCurrencyCode
            customerTotalOutstanding
            customerCurrencyTotalOutstanding
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
`;

const AGEING_ANALYSIS_COUNT_QUERY = `
  query AgeingAnalysisCount($filter: CustomerAgeingAnalysisFilterInput) {
    customerAgeingAnalysis {
      customerAgeingAnalysisCount(filter: $filter)
    }
  }
`;

// --- Sort field map ---

const sortFieldMap: Record<string, string> = {
  accountNumber: 'AccountNumber',
  customerName: 'CustomerName',
  totalOutstanding: 'TotalOutstanding',
  creditLimit: 'CreditLimit',
  riskLevel: 'RiskLevelCode',
  collectionStrategy: 'CollectionStrategyName',
  creditController: 'CreditControllerName',
  accountManager: 'AccountManagerName',
  currency: 'CurrencyCode',
  terms: 'PaymentTermsCode',
  lastPayment: 'LastPaymentDate',
  sequence: 'Sequence',
};

// --- Filter builder ---

function buildFilterVariable(params: GetAgeingAnalysisParams) {
  const filter: Record<string, unknown> = {};
  const filters = params.filters;

  if (params.search) {
    filter.summary = params.search;
  }
  if (filters?.customerIds && filters.customerIds.length > 0) {
    const numericIds = filters.customerIds.map(Number).filter((n) => !isNaN(n));
    if (numericIds.length > 0) {
      filter.customerId = { containsValues: numericIds };
    }
  }
  if (filters?.riskLevel && filters.riskLevel.length > 0) {
    filter.riskLevelCode = { containsValues: filters.riskLevel };
  }
  if (filters?.currency && filters.currency.length > 0) {
    filter.currencyCode = { containsValues: filters.currency };
  }
  if (filters?.creditController && filters.creditController.length > 0) {
    filter.creditControllerName = { containsValues: filters.creditController };
  }
  if (filters?.accountManager && filters.accountManager.length > 0) {
    filter.accountManagerName = { containsValues: filters.accountManager };
  }
  if (filters?.collectionStrategy && filters.collectionStrategy.length > 0) {
    filter.collectionStrategyName = { containsValues: filters.collectionStrategy };
  }
  if (filters?.terms && filters.terms.length > 0) {
    filter.paymentTermsCode = { containsValues: filters.terms };
  }
  if (filters?.outstandingMin !== undefined && filters?.outstandingMin !== null) {
    filter.totalOutstanding = {
      ...((filter.totalOutstanding as Record<string, unknown>) ?? {}),
      ge: filters.outstandingMin,
    };
  }
  if (filters?.outstandingMax !== undefined && filters?.outstandingMax !== null) {
    filter.totalOutstanding = {
      ...((filter.totalOutstanding as Record<string, unknown>) ?? {}),
      le: filters.outstandingMax,
    };
  }
  if (filters?.creditLimitMin !== undefined && filters?.creditLimitMin !== null) {
    filter.creditLimit = { ...((filter.creditLimit as Record<string, unknown>) ?? {}), ge: filters.creditLimitMin };
  }
  if (filters?.creditLimitMax !== undefined && filters?.creditLimitMax !== null) {
    filter.creditLimit = { ...((filter.creditLimit as Record<string, unknown>) ?? {}), le: filters.creditLimitMax };
  }
  if (filters?.lastPaymentFrom) {
    filter.lastPaymentDate = {
      ...((filter.lastPaymentDate as Record<string, unknown>) ?? {}),
      ge: filters.lastPaymentFrom,
    };
  }
  if (filters?.lastPaymentTo) {
    filter.lastPaymentDate = {
      ...((filter.lastPaymentDate as Record<string, unknown>) ?? {}),
      le: filters.lastPaymentTo,
    };
  }
  if (filters?.userSortKey1 && filters.userSortKey1.length > 0) {
    filter.userSortKey1 = { containsValues: filters.userSortKey1 };
  }
  if (filters?.userSortKey2 && filters.userSortKey2.length > 0) {
    filter.userSortKey2 = { containsValues: filters.userSortKey2 };
  }
  if (filters?.userSortKey3 && filters.userSortKey3.length > 0) {
    filter.userSortKey3 = { containsValues: filters.userSortKey3 };
  }
  if (filters?.userSortKey4 && filters.userSortKey4.length > 0) {
    filter.userSortKey4 = { containsValues: filters.userSortKey4 };
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
}

// --- Pivoting: group per-band rows into per-customer objects ---

function pivotToCustomers(nodes: AgeingAnalysisGraphQLNode[]): CustomerAgeingData[] {
  const customerMap = new Map<number, CustomerAgeingData>();

  for (const node of nodes) {
    let customer = customerMap.get(node.customerId);

    if (!customer) {
      customer = {
        id: String(node.customerId),
        customerId: node.customerId,
        customerName: node.customerName ?? '',
        accountNumber: node.accountNumber ?? '',
        creditLimit: node.creditLimit,
        currencyCreditLimit: node.currencyCreditLimit,
        totalOutstanding: node.customerCurrencyTotalOutstanding,
        ageing: { current: 0, days31_60: 0, days61_90: 0, days91_120: 0, over120: 0 },
        bands: {},
        baseBands: {},
        baseTotalOutstanding: node.customerTotalOutstanding,
        homeCurrencyCode: node.homeCurrencyCode ?? 'GBP',
        averageDaysOverdue: 0,
        worstDaysOverdue: 0,
        lastContactDate: new Date().toISOString(),
        riskLevel: (node.riskLevelCode?.toLowerCase() as CustomerAgeingData['riskLevel']) || 'none',
        aiPriorityScore: 0,
        collectionStrategy: node.collectionStrategyName ?? '',
        statusFlags: {
          hasDispute: false,
          hasPromiseToPay: false,
          hasCollectionCase: false,
          isOverdueAlert: false,
          noRecentContact: false,
        },
        currency: node.currencyCode ?? 'GBP',
        contact: node.contactEmail ?? '',
        phone: node.contactPhone ?? undefined,
        lastPayment: node.lastPaymentDate ?? undefined,
        creditControllerName: node.creditControllerName ?? undefined,
        accountManagerName: node.accountManagerName ?? undefined,
        userSortKey1: node.userSortKey1 ?? undefined,
        userSortKey2: node.userSortKey2 ?? undefined,
        userSortKey3: node.userSortKey3 ?? undefined,
        userSortKey4: node.userSortKey4 ?? undefined,
        terms: node.paymentTermsCode ?? '',
      };
      customerMap.set(node.customerId, customer);
    }

    customer.bands[node.sequence] = (customer.bands[node.sequence] ?? 0) + node.currencyTotalOutstanding;
    customer.baseBands[node.sequence] = (customer.baseBands[node.sequence] ?? 0) + node.totalOutstanding;

    if (node.startDays <= 30) {
      customer.ageing.current += node.currencyTotalOutstanding;
    } else if (node.startDays <= 60) {
      customer.ageing.days31_60 += node.currencyTotalOutstanding;
    } else if (node.startDays <= 90) {
      customer.ageing.days61_90 += node.currencyTotalOutstanding;
    } else if (node.startDays <= 120) {
      customer.ageing.days91_120 += node.currencyTotalOutstanding;
    } else {
      customer.ageing.over120 += node.currencyTotalOutstanding;
    }

    const endDays = node.endDays ?? node.startDays + 30;
    if (endDays > customer.worstDaysOverdue && node.totalOutstanding > 0) {
      customer.worstDaysOverdue = endDays;
    }
  }

  return Array.from(customerMap.values());
}

// --- Mock helpers ---

let cachedMockCustomers: CustomerAgeingData[] | null = null;

function getMockCustomers(): CustomerAgeingData[] {
  if (!cachedMockCustomers) {
    cachedMockCustomers = generateMockCustomers();
  }
  return cachedMockCustomers;
}

function filterMockCustomers(customers: CustomerAgeingData[], params: GetAgeingAnalysisParams): CustomerAgeingData[] {
  return customers.filter((x) => {
    const filters = params.filters;
    const matchesSearch =
      !params.search ||
      x.customerName.toLowerCase().includes(params.search.toLowerCase()) ||
      x.accountNumber.toLowerCase().includes(params.search.toLowerCase());
    const matchesRisk =
      !filters?.riskLevel || filters.riskLevel.length === 0 || filters.riskLevel.includes(x.riskLevel);
    const matchesCurrency =
      !filters?.currency || filters.currency.length === 0 || filters.currency.includes(x.currency);
    const matchesTerms = !filters?.terms || filters.terms.length === 0 || filters.terms.includes(x.terms);
    const matchesController =
      !filters?.creditController ||
      filters.creditController.length === 0 ||
      filters.creditController.includes(x.creditControllerName ?? '');
    const matchesManager =
      !filters?.accountManager ||
      filters.accountManager.length === 0 ||
      filters.accountManager.includes(x.accountManagerName ?? '');
    const matchesStrategy =
      !filters?.collectionStrategy ||
      filters.collectionStrategy.length === 0 ||
      filters.collectionStrategy.includes(x.collectionStrategy);
    const matchesOutstandingMin =
      filters?.outstandingMin === null ||
      filters?.outstandingMin === undefined ||
      x.totalOutstanding >= filters.outstandingMin;
    const matchesOutstandingMax =
      filters?.outstandingMax === null ||
      filters?.outstandingMax === undefined ||
      x.totalOutstanding <= filters.outstandingMax;
    const matchesCreditLimitMin =
      filters?.creditLimitMin === null ||
      filters?.creditLimitMin === undefined ||
      x.creditLimit >= filters.creditLimitMin;
    const matchesCreditLimitMax =
      filters?.creditLimitMax === null ||
      filters?.creditLimitMax === undefined ||
      x.creditLimit <= filters.creditLimitMax;

    return (
      matchesSearch &&
      matchesRisk &&
      matchesCurrency &&
      matchesTerms &&
      matchesController &&
      matchesManager &&
      matchesStrategy &&
      matchesOutstandingMin &&
      matchesOutstandingMax &&
      matchesCreditLimitMin &&
      matchesCreditLimitMax
    );
  });
}

function sortMockCustomers(
  customers: CustomerAgeingData[],
  sortBy?: string,
  sortOrder?: 'asc' | 'desc',
): CustomerAgeingData[] {
  if (!sortBy) return customers;

  const sorted = [...customers].sort((a, b) => {
    let aVal: unknown;
    let bVal: unknown;

    if (sortBy === 'totalOutstanding') {
      // Sort by base currency so values are comparable across different currencies
      aVal = a.baseTotalOutstanding;
      bVal = b.baseTotalOutstanding;
    } else if (sortBy.startsWith('band_')) {
      // Handle dynamic band columns (band_0, band_1, etc.)
      const sequence = Number(sortBy.replace('band_', ''));
      aVal = a.baseBands[sequence] ?? 0;
      bVal = b.baseBands[sequence] ?? 0;
    } else {
      aVal = (a as unknown as Record<string, unknown>)[sortBy];
      bVal = (b as unknown as Record<string, unknown>)[sortBy];
    }

    if (aVal === bVal) return 0;
    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;
    return aVal < bVal ? -1 : 1;
  });

  return sortOrder === 'desc' ? sorted.reverse() : sorted;
}

// --- Result types ---

export interface AgeingAnalysisListResult {
  customers: CustomerAgeingData[];
  pagination: PaginationResult;
}

// --- Hooks ---

function useAgeingAnalysisCount(params?: GetAgeingAnalysisParams) {
  const apiConfig = useApiConfig();
  const filter = buildFilterVariable(params ?? {});

  return useModeAwareQuery<number, Error>(
    ['ageing-analysis-count', filter],
    async ({ signal }) => {
      const variables: Record<string, unknown> = {};
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<AgeingAnalysisCountResponse>(
        apiConfig,
        AGEING_ANALYSIS_COUNT_QUERY,
        variables,
        signal,
      );

      return response.customerAgeingAnalysis.customerAgeingAnalysisCount;
    },
    0,
    { staleTime: 30_000, refetchOnMount: 'always' },
  );
}

export function useAgeingAnalysisListGraphQL(params?: GetAgeingAnalysisParams) {
  const apiConfig = useApiConfig();
  const mockCustomers = getMockCustomers();
  const queryParams: GetAgeingAnalysisParams = {
    page: 1,
    pageSize: 25,
    sortBy: 'customerName',
    sortOrder: 'asc',
    ...params,
  };
  const mockPage = queryParams.page ?? 1;
  const mockPageSize = queryParams.pageSize ?? 25;
  const filteredMock = filterMockCustomers(mockCustomers, queryParams);
  const sortedMock = sortMockCustomers(filteredMock, queryParams.sortBy, queryParams.sortOrder);
  const mockPagination = computeMockPagination(sortedMock.length, mockPage, mockPageSize);
  const mockPagedCustomers = sortedMock.slice((mockPage - 1) * mockPageSize, mockPage * mockPageSize);
  const mockResult: AgeingAnalysisListResult = { customers: mockPagedCustomers, pagination: mockPagination };

  // The query key excludes page/pageSize because in live mode we fetch ALL rows
  // and paginate client-side after pivoting. The SQL view returns one row per
  // customer per ageing band, so row-level cursor pagination would split a
  // customer's band rows across pages, causing duplicates. Fetching everything
  // lets pivotToCustomers correctly merge all bands per customer.
  const filter = buildFilterVariable(queryParams);
  // Only pass sort to the API when the field is a known backend enum value.
  // Band columns (band_0, band_1, …) are sorted client-side after pivoting.
  const apiSortBy = queryParams.sortBy && sortFieldMap[queryParams.sortBy] ? queryParams.sortBy : undefined;
  const sort = buildGraphQLSortVariable(sortFieldMap, apiSortBy, queryParams.sortOrder);
  const liveQueryKey = ['ageing-analysis-list', filter, sort, queryParams.sortBy, queryParams.sortOrder];

  return useModeAwareQuery<AgeingAnalysisListResult, Error>(
    liveQueryKey,
    async ({ signal }) => {
      const pageSize = queryParams.pageSize ?? 25;

      const variables: Record<string, unknown> = {
        first: 10000,
        sort,
        filter,
      };

      const response = await graphqlQuery<AgeingAnalysisListResponse>(
        apiConfig,
        AGEING_ANALYSIS_LIST_QUERY,
        variables,
        signal,
      );

      const connection = response.customerAgeingAnalysis.customerAgeingAnalysisList;
      const nodes = connection.edges.map((x) => x.node);
      const customers = sortMockCustomers(pivotToCustomers(nodes), queryParams.sortBy, queryParams.sortOrder);

      const pagination: PaginationResult = {
        page: 1,
        pageSize,
        totalItems: customers.length,
        totalPages: Math.max(1, Math.ceil(customers.length / pageSize)),
        hasNextPage: false,
        hasPreviousPage: false,
      };

      return { customers, pagination };
    },
    mockResult,
    { staleTime: 0, refetchOnMount: 'always' },
  );
}

// --- Composite hook ---

const defaultPagination = createDefaultPagination(25);

export function useAgeingAnalysisPageData(params?: GetAgeingAnalysisParams) {
  const listQuery = useAgeingAnalysisListGraphQL(params);

  return {
    customers: listQuery.data?.customers ?? [],
    pagination: listQuery.data?.pagination ?? defaultPagination,
    isLoading: listQuery.isLoading,
    error: listQuery.error,
    refetch: () => {
      listQuery.refetch();
    },
  };
}
